import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { PencilSimple, X, Check, CircleNotch } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const pill = "text-xs uppercase tracking-wider font-mono border px-3 py-1.5";

const Editor = ({ video, onClose }) => {
  const qc = useQueryClient();
  const [tags, setTags] = useState(video.keywords || []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const add = (raw) => {
    const next = raw.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
    if (next.length) setTags((cur) => [...new Set([...cur, ...next])]);
    setDraft("");
  };
  const save = async () => {
    setBusy(true);
    try {
      const all = draft.trim() ? [...new Set([...tags, ...draft.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean)])] : tags;
      const { data } = await axios.put(`${API}/admin/media/${video.video_id}/tags`, { keywords: all }, { withCredentials: true });
      qc.setQueryData(["video", video.video_id], (v) => (v ? { ...v, keywords: data.keywords } : v));
      toast.success("Tags saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save tags");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tag-editor" className="flex flex-wrap items-center gap-2">
      {tags.map((t) => (
        <span key={t} data-testid={`tag-edit-pill-${t}`} className={`${pill} flex items-center gap-1.5 border-amber/40 text-amber`}>
          {t}
          <button type="button" data-testid={`tag-remove-${t}`} aria-label={`Remove ${t}`} onClick={() => setTags((c) => c.filter((x) => x !== t))}
            className="text-white/50 hover:text-crimson transition-colors duration-200"><X size={11} weight="bold" /></button>
        </span>
      ))}
      <input
        data-testid="tag-editor-input"
        value={draft}
        autoFocus
        placeholder="add tags, comma separated"
        onChange={(e) => (e.target.value.endsWith(",") ? add(e.target.value) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); add(draft); }
          if (e.key === "Escape") onClose();
        }}
        className="w-56 bg-[#111] border border-white/15 px-3 py-1.5 font-mono text-xs text-white focus:outline-none focus:border-amber"
      />
      <button type="button" data-testid="tag-editor-save" onClick={save} disabled={busy}
        className="flex items-center gap-1.5 bg-amber px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] font-bold text-black hover:bg-amber/80 transition-colors duration-200 disabled:opacity-50">
        {busy ? <CircleNotch size={13} className="animate-spin" /> : <Check size={13} weight="bold" />} Save
      </button>
      <button type="button" data-testid="tag-editor-cancel" onClick={onClose}
        className="px-2 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white/50 hover:text-white transition-colors duration-200">
        Cancel
      </button>
    </div>
  );
};

// Watch-page tags: read-only pills for everyone; owner gets an inline editor behind the pencil.
export const VideoTags = ({ video, renderPills }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const admin = !!user?.is_admin;
  return (
    <div data-testid="video-tags" className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/30">Tags</span>
      {admin && !editing && (
        <button type="button" data-testid="tag-edit-btn" aria-label="Edit tags" onClick={() => setEditing(true)}
          className="mr-2 p-1 text-white/35 hover:text-amber transition-colors duration-200">
          <PencilSimple size={14} weight="bold" />
        </button>
      )}
      {!admin && <span className="mr-2" />}
      {admin && editing ? <Editor video={video} onClose={() => setEditing(false)} /> : renderPills()}
    </div>
  );
};
